import * as Api from '@ViewModels';
import * as React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { invalidateZoomConnectionQuery, useCallbackAuthorizeUrlMutation } from '../../../../queries';
import { ZOOM_CONNECT_RETURN_PATH_KEY } from './constants';

export function useZoomConnect({
	connectReturnPath,
	feature,
	onConnected,
}: {
	/** Must be a route that mounts a consumer of this hook, or the callback outcome is never handled. */
	connectReturnPath: string;
	feature?: string;
	onConnected?: () => void;
}) {
	const history = useHistory();
	const location = useLocation<{ command?: string; outcome?: string } | undefined>();

	const authorizeUrlMutation = useCallbackAuthorizeUrlMutation({
		onError: error => {
			// Clear the stashed return path so a failed start cannot redirect a later connect from another surface.
			sessionStorage.removeItem(ZOOM_CONNECT_RETURN_PATH_KEY);
			toast.error(error.systemMessage ?? 'Unable to start Zoom connect. Please try again.');
		},
		onSuccess: url => window.location.replace(url),
	});

	React.useEffect(() => {
		const state = location.state;
		if (state?.command !== 'configCallback' || !state.outcome) {
			return;
		}
		if (state.outcome === 'success') {
			toast.success('Zoom connected');
			invalidateZoomConnectionQuery();
			onConnected?.();
		} else {
			// `outcome` comes from the callback URL, so never render it: a crafted link could put its text in the toast.
			toast.error('Unable to connect Zoom. Please try again.');
		}
		// Cleared so the toast does not re-fire on the next render.
		history.replace({ ...location, state: undefined });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location.state]);

	const connect = () => {
		sessionStorage.setItem(ZOOM_CONNECT_RETURN_PATH_KEY, connectReturnPath);
		authorizeUrlMutation.mutate({ provider: Api.IntegrationProvider.Zoom, feature });
	};

	return { connect, isConnecting: authorizeUrlMutation.isPending };
}
